/**
 * AuthContext.js — Authentication State Management
 *
 * Extracted from App.js to isolate auth concern.
 * Manages the current user, auth token, first-login flow, and
 * session persistence to localStorage.
 *
 * App.js remains responsible for resetting allowedViews / nav state
 * in response to auth changes — it consumes this context via useAuth().
 *
 * Usage:
 *   const { currentUser, authToken, login, logout, ... } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

// Bump this whenever the stored session shape changes to auto-clear stale sessions.
const SESSION_VERSION = 2;

const AuthContext = createContext();

/**
 * Reads and validates the stored session synchronously.
 *
 * This used to happen in a mount effect, which meant the first render
 * always saw currentUser === null — so a page refresh (or any deep link)
 * bounced through /login and then landed on the default view instead of
 * the URL the user actually asked for. Reading synchronously in the
 * useState initializer keeps the user on the page they refreshed.
 */
function readStoredSession() {
  try {
    const raw = localStorage.getItem('fleet-session');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== SESSION_VERSION) {
      localStorage.removeItem('fleet-session');
      return null;
    }
    return parsed;
  } catch {
    localStorage.removeItem('fleet-session');
    return null;
  }
}

export function AuthProvider({ children }) {
  const queryClient = useQueryClient();

  // Current authenticated user object; null means not logged in.
  // Initialized synchronously from localStorage — see readStoredSession.
  const [currentUser, setCurrentUser] = useState(() => readStoredSession()?.user || null);
  const [authToken, setAuthToken]     = useState(() => readStoredSession()?.token || '');

  // Set when a user logs in for the first time (userState === 1).
  // While truthy, the app shows FirstLoginPage instead of the main shell.
  const [firstLoginSession, setFirstLoginSession] = useState(null);

  // ── Session persistence ────────────────────────────────────────────────────
  useEffect(() => {
    if (!currentUser || !authToken) {
      localStorage.removeItem('fleet-session');
      return;
    }
    localStorage.setItem(
      'fleet-session',
      JSON.stringify({ version: SESSION_VERSION, user: currentUser, token: authToken })
    );
  }, [currentUser, authToken]);

  // ── Auth actions ───────────────────────────────────────────────────────────

  /** Called after a successful login. Sets auth state; App.js reacts via useEffect. */
  const login = (token, user) => {
    setAuthToken(token);
    setCurrentUser(user);
  };

  /**
   * Clears all authentication state.
   * App.js reacts to currentUser becoming null to reset navigation state.
   */
  const logout = () => {
    queryClient.clear();
    setAuthToken('');
    setCurrentUser(null);
    setFirstLoginSession(null);
  };

  /** Called by the API layer when a 401 is received — clears session cleanly. */
  const signalSessionExpired = () => logout();

  /**
   * Called when a first-time user completes the password-set flow.
   * Clears the first-login gate and promotes the user to a normal session.
   */
  const completeFirstLogin = (token, user) => {
    setFirstLoginSession(null);
    setAuthToken(token);
    setCurrentUser(user);
  };

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        authToken,
        firstLoginSession,
        setFirstLoginSession,
        login,
        logout,
        completeFirstLogin,
        signalSessionExpired,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
